import { HTTP_STATUS } from '../domain/domainCodes.js'
import { loginDestination } from './loginReturn'
/**
 * The one place this app talks to the network.
 *
 * The API is a .NET minimal-API host that also serves this bundle, so every call is
 * SAME-ORIGIN: no CORS, no base URL, and the HttpOnly `AccessToken` cookie rides along
 * on its own. That cookie is the whole authentication story — nothing here reads,
 * stores or parses a token, and "signed in" means `GET /api/session` answered 200.
 *
 * Four things this wrapper exists to get right, each of which cost a real bug in the
 * legacy panel:
 *
 * 1. PASCALCASE IS LEFT ALONE. The server sets `PropertyNamingPolicy = null`, so the
 *    wire is `{ UserName, IsApproved }`. Renaming it here would put a second, invisible
 *    contract between the endpoint and the screen. Service modules map to the component
 *    shape; this layer hands back exactly what arrived.
 *
 * 2. FAILURE HAS THREE SHAPES AND ONE MEANING. `Results.Problem` answers
 *    `{ title, detail }` with a 4xx/5xx, action endpoints answer 200 with
 *    `{ Result: false, Message }`, and DataTables endpoints answer 200 with
 *    `{ Error }`. All three become one thrown `ApiError`, so a caller only ever writes
 *    one catch.
 *
 * 3. HTML IS NEVER SILENTLY PARSED AS DATA. `ForceRedirectionMiddleware` answers 302 to
 *    a gated user and the login page is HTML — a plain `fetch` follows that and hands
 *    back a web page where JSON was expected, which surfaces three layers later as
 *    "cannot read property of undefined". `redirect: 'manual'` plus a content-type
 *    check turns both cases into a typed error at the call site.
 *
 * 4. A TOKEN NEVER EXPIRES MID-FORM IN SILENCE. `ClockSkew` is zero and the JWT has a
 *    fixed lifetime, so it simply stops working one day. `POST /api/session/refresh` is
 *    called ahead of the expiry the session bootstrap reported, and at most once more in
 *    response to a 401. A failed refresh always ENDS the session — never a retry loop.
 */

import { transport } from '../components/strings'
import { locale } from './i18n'
import { forgetListState } from '../components/listMemory'

/** Refresh this long before `TokenExpiresAt` rather than racing it. */
const REFRESH_MARGIN_MS = 5 * 60 * 1000

export class ApiError extends Error {
  /**
   * @param {string} message      what to show the user; a server message is used as-is (W6)
   * @param {object} [info]
   * @param {number} [info.status]
   * @param {string} [info.code]     'HTTP' | 'PROBLEM' | 'RESULT' | 'HTML' | 'REDIRECT' | 'NETWORK' | 'SESSION'
   * @param {boolean} [info.denied]  403 — the screen shows its permission-denied state
   * @param {any}    [info.data]     the parsed body, when there was one
   */
  constructor(message, { status = 0, code = 'HTTP', denied = false, data = null } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.denied = denied
    this.data = data
  }
}

/* ------------------------------------------------------------------ *
 * Session hooks — set by SessionProvider, defaulted so this module is
 * usable (and testable) on its own.
 * ------------------------------------------------------------------ */

let tokenExpiresAt = null
let rememberMe = false
let onSessionEnded = () => {
  // A full navigation, not a router push: the session is gone, so the safest thing
  // is to let the server decide what /login looks like from scratch.
  if (window.location.pathname !== '/login') window.location.assign(loginDestination())
}

/** Called by the session bootstrap and after every successful refresh. */
export function noteTokenExpiry(isoOrDate, remember = rememberMe) {
  tokenExpiresAt = isoOrDate ? new Date(isoOrDate) : null
  rememberMe = remember
}

/** Called when the session ends for any reason — expiry, sign-out, a refused refresh. */
export function setSessionEndedHandler(handler) {
  onSessionEnded = handler
}

/* ------------------------------------------------------------------ *
 * The token lifecycle
 * ------------------------------------------------------------------ */

/** In flight, so ten parallel calls at boot cause one refresh and not ten. */
let refreshInFlight = null

function refresh() {
  refreshInFlight ??= rawRequest('/api/session/refresh', {
    method: 'POST',
    body: { RememberMe: rememberMe },
    // Without this the 401 handler would call refresh to recover from refresh.
    skipRefresh: true,
  })
    .then((data) => {
      noteTokenExpiry(data?.ExpiresAt)
      return true
    })
    .catch(() => {
      tokenExpiresAt = null
      return false
    })
    .finally(() => {
      refreshInFlight = null
    })

  return refreshInFlight
}

/** Refresh BEFORE the request when the token is about to lapse. */
async function refreshIfExpiring() {
  if (!tokenExpiresAt) return
  if (tokenExpiresAt.getTime() - Date.now() > REFRESH_MARGIN_MS) return
  await refresh()
}

/* ------------------------------------------------------------------ *
 * The request
 * ------------------------------------------------------------------ */

/**
 * @param {string} path
 * @param {object} [options]
 * @param {'GET'|'POST'|'PUT'|'DELETE'} [options.method]
 * @param {any}    [options.body]         a plain object is sent as JSON; FormData,
 *                                        URLSearchParams and a Blob (a File) are sent
 *                                        as-is (see form.js)
 * @param {object} [options.headers]
 * @param {AbortSignal} [options.signal]
 * @param {boolean} [options.anonymousOk]  a 401 resolves to null instead of ending the
 *                                         session — only the session bootstrap wants
 *                                         this, since "not signed in" is an answer there
 * @param {boolean} [options.skipRefresh]  internal — used by the refresh call itself
 */
async function rawRequest(path, { method = 'GET', body, headers, signal, anonymousOk = false, skipRefresh = false } = {}) {
  const init = {
    method,
    signal,
    // The cookie is the credential. Same-origin is deliberate: `include` would also
    // send it cross-origin, which this app never needs and should never do.
    credentials: 'same-origin',
    // Do not chase a 302 into an HTML page. See note 3 at the top.
    redirect: 'manual',
    /*
     * `Accept-Language` is the panel's language, NOT the browser's.
     *
     * It is the server's third tier after the query string and `.AspNetCore.Culture`, so
     * it changes nothing while that cookie stands. It decides when the cookie does not:
     * a private window, cleared site data, a browser that refuses cookies to `document`.
     * Without it the request carried whatever the BROWSER asks for — Arabic, for most of
     * this product's users — and an English panel got Arabic messages back. See
     * `writeCultureCookie` in i18n.js for the cookie half.
     */
    headers: { Accept: 'application/json', 'Accept-Language': locale.culture, ...headers },
  }

  if (body !== undefined && body !== null) {
    if (
      body instanceof FormData ||
      body instanceof URLSearchParams ||
      body instanceof Blob ||
      typeof body === 'string'
    ) {
      // FormData sets its own multipart boundary; URLSearchParams sets
      // x-www-form-urlencoded. Setting Content-Type by hand breaks both. A Blob is
      // raw bytes — the caller names the type in `headers`.
      init.body = body
    } else {
      init.headers['Content-Type'] = 'application/json'
      init.body = JSON.stringify(body)
    }
  }

  let response
  try {
    response = await fetch(path, init)
  } catch (error) {
    if (error?.name === 'AbortError') throw error
    throw new ApiError(transport.network, { code: 'NETWORK' })
  }

  // `redirect: 'manual'` turns a 3xx into an opaque response we cannot read. That is
  // the force-redirection gate or a sign-in bounce, and either way this call has no
  // answer — say so instead of returning something shaped like data.
  if (response.type === 'opaqueredirect' || (response.status === 0 && !response.ok)) {
    throw new ApiError(transport.redirected, { code: 'REDIRECT' })
  }

  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    // The bootstrap asks "am I signed in"; no is a valid answer, not a failure, and
    // refreshing a session that does not exist would just be a second 401.
    if (anonymousOk) return null

    if (skipRefresh || !(await refresh())) {
      onSessionEnded()
      throw new ApiError(transport.sessionEnded, { status: HTTP_STATUS.UNAUTHORIZED, code: 'SESSION' })
    }
    // One retry, with the fresh cookie, and this time no second chance.
    return rawRequest(path, { method, body, headers, signal, skipRefresh: true })
  }

  return readBody(response)
}

/** Turns the response into data, or into the one ApiError shape. */
async function readBody(response) {
  const contentType = response.headers.get('Content-Type') ?? ''
  const isJson = contentType.includes('json')

  if (response.status === HTTP_STATUS.FORBIDDEN) {
    throw new ApiError(await problemMessage(response, isJson, transport.denied), {
      status: HTTP_STATUS.FORBIDDEN,
      code: 'PROBLEM',
      denied: true,
    })
  }

  if (response.status === HTTP_STATUS.NO_CONTENT) return null

  // An HTML body where JSON was asked for is a page — the login screen, an error
  // page, the SPA shell. Never hand it back as data.
  if (!isJson && contentType.includes('html')) {
    throw new ApiError(transport.html, {
      status: response.status,
      code: 'HTML',
    })
  }

  if (!isJson) {
    const text = await response.text()
    if (!response.ok) {
      warnIfUnproxied(response)
      throw new ApiError(text || transport.failed(response.status), {
        status: response.status,
        code: 'HTTP',
      })
    }
    return text
  }

  const data = await response.json().catch(() => null)

  if (!response.ok) {
    throw new ApiError(problemDetail(data) ?? transport.failed(response.status), {
      status: response.status,
      code: 'PROBLEM',
      data,
    })
  }

  // A 200 that means "no". Both flavours the endpoints use.
  if (data && typeof data === 'object') {
    if (data.Result === false) {
      /* The server said no and gave no reason. `failed(200)` rather than a sentence of its
         own: the status is what a support call is opened with, and inventing wording here
         would read as though the app knew something it does not. */
      throw new ApiError(data.Message || transport.failed(200), { status: HTTP_STATUS.OK, code: 'RESULT', data })
    }
    if (typeof data.Error === 'string' && data.Error) {
      throw new ApiError(data.Error, { status: HTTP_STATUS.OK, code: 'RESULT', data })
    }
  }

  return data
}

/**
 * The one failure that is never the API's fault, and never says so.
 *
 * Most action endpoints do NOT live under `/api` — they sit beside their slice's page
 * route. In dev those only reach the server if `vite.config.js` proxies them, and an
 * unproxied one comes back as a bodyless 404 from Vite. On screen that is an unexplained
 * "system error"; in the network tab it looks like the endpoint is simply missing.
 *
 * Development only: in production everything is same-origin and there is no proxy.
 */
function warnIfUnproxied(response) {
  if (!import.meta.env.DEV || response.status !== HTTP_STATUS.NOT_FOUND) return

  const path = new URL(response.url).pathname
  if (path.startsWith('/api')) return

  console.warn(
    `[api] ${path} 404'd. It is not under /api, so the dev server only forwards it ` +
      'if it is listed in `proxied` in vite.config.js. Add it there and restart the dev server.',
  )
}

/** `Results.Problem` puts the useful sentence in `detail`. */
function problemDetail(data) {
  if (!data || typeof data !== 'object') return null

  /* A FAILED VALIDATION IS A BARE ARRAY. `RawamValidationException` serializes its
     `ValidationErrors` list and nothing else, so a 400 from the validation pipeline has no
     `detail` to read — every reason would have been dropped for the generic "system error"
     sentence. The lines are shown as received, one per line. */
  if (Array.isArray(data)) {
    const lines = data.filter((line) => typeof line === 'string' && line)
    return lines.length > 0 ? lines.join('\n') : null
  }

  return data.detail || data.Detail || data.title || data.Title || data.Message || null
}

async function problemMessage(response, isJson, fallback) {
  if (!isJson) return fallback
  const data = await response.json().catch(() => null)
  return problemDetail(data) ?? fallback
}

/* ------------------------------------------------------------------ *
 * The surface service modules use
 * ------------------------------------------------------------------ */

async function request(path, options) {
  if (!options?.skipRefresh) await refreshIfExpiring()
  return rawRequest(path, options)
}

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, body, options) => request(path, { ...options, method: 'DELETE', body }),
}

/**
 * Ends the session.
 *
 * `POST /Logout` already answers `{ Result, RedirectUrl }`, so nothing new was needed on
 * the server for sign-out. The call MATTERS: the auth cookie is HttpOnly, so only the
 * server can clear it — a client-side "log out" that merely navigates to `/login` leaves
 * the user signed in, and the next request walks straight back into the app.
 *
 * Where to land is the server's answer (`/Login` today), and a full navigation rather
 * than a router push, so nothing of the previous user's session survives in memory.
 */
export async function signOut() {
  let redirectTo = '/login'

  try {
    const data = await api.post('/Logout')
    if (data?.RedirectUrl) redirectTo = data.RedirectUrl
  } catch {
    /* The session is over as far as this app is concerned either way — never trap
       someone on a screen they have already left. */
  }

  tokenExpiresAt = null
  /* Where every list was left goes with the session: the tab outlives the sign-out, and
     the next person to use it should not inherit the last one's filters. */
  forgetListState()
  window.location.assign(redirectTo)
}
